
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Check, X, AlertTriangle, ExternalLink, Calendar, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Run } from '@/features/runs/types';

interface RecentRunsProps {
  runs: Run[];
  onRowClick?: (id: string) => void;
}

const StatusIcon: React.FC<{ status: Run['status'] }> = ({ status }) => {
  switch (status) {
    case 'passed':
      return <Check className="h-4 w-4 text-accent-teal" />;
    case 'failed':
      return <X className="h-4 w-4 text-status-red" />;
    case 'warning':
      return <AlertTriangle className="h-4 w-4 text-status-yellow" />;
    default:
      return null;
  }
};

const RecentRuns: React.FC<RecentRunsProps> = ({ runs, onRowClick }) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent Runs</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left font-medium py-2 pl-4">Status</th>
                <th className="text-left font-medium py-2">Suite</th>
                <th className="text-left font-medium py-2">Commit</th>
                <th className="text-left font-medium py-2">Runtime</th>
                <th className="text-left font-medium py-2">Trigger</th>
                <th className="text-left font-medium py-2 pr-4">Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr 
                  key={run.id} 
                  className="border-b hover:bg-muted/50 cursor-pointer transition-colors even:bg-muted/5"
                  onClick={() => onRowClick?.(run.id)}
                >
                  <td className="py-3 pl-4">
                    <div className="flex items-center gap-2">
                      <div 
                        className={`w-2.5 h-2.5 rounded-full ${
                          run.status === 'passed' ? 'bg-accent-teal' : 
                          run.status === 'failed' ? 'bg-status-red' : 'bg-status-yellow'
                        }`}
                      />
                      <StatusIcon status={run.status} />
                    </div>
                  </td>
                  <td className="py-3 font-medium">{run.suite}</td>
                  <td className="py-3">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <a 
                            href={`https://github.com/org/repo/commit/${run.commit}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono text-xs bg-secondary px-2 py-1 rounded flex items-center gap-1 hover:bg-secondary/80 w-fit"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span>{run.commit}</span>
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>View on GitHub</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </td>
                  <td className="py-3">{run.runtime}</td>
                  <td className="py-3">
                    <span className={cn(
                      "px-2 py-1 rounded-full text-xs",
                      run.trigger === 'CI' 
                        ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400" 
                        : "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400"
                    )}>
                      {run.trigger}
                    </span>
                  </td>
                  <td className="py-3 pr-4">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-3 w-3 text-muted-foreground" />
                            <span>{run.timestamp}</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{run.dateTime} UTC</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default RecentRuns;
